/**
 * SoundService - Manages sound effects playback
 * 
 * Plays sounds based on user settings. Gracefully handles missing sound files.
 */

import { storageService } from './StorageService';

// Base path for sounds - works with both public folder and deployed builds
const SOUND_BASE_PATH = process.env.PUBLIC_URL || '';

// Sound types and their file paths
const SOUNDS = {
  success: `${SOUND_BASE_PATH}/assets/media/sounds/success.mp3`,
  error: `${SOUND_BASE_PATH}/assets/media/sounds/error.mp3`,
  click: `${SOUND_BASE_PATH}/assets/media/sounds/click.mp3`,
  achievement: `${SOUND_BASE_PATH}/assets/media/sounds/achievement.mp3`,
  levelup: `${SOUND_BASE_PATH}/assets/media/sounds/levelup.mp3`
};

class SoundService {
  constructor() {
    this.audioCache = {};
    this.initialized = false;
    this.enabled = true;
    
    // Auto-initialize on construction
    this.initialize();
  }

  /**
   * Initialize the sound service and preload sounds
   */
  initialize() {
    if (this.initialized) return;

    // Load enabled state from settings
    this.enabled = storageService.getSetting('soundEnabled', true);

    // Preload sounds
    this.preloadSounds();

    this.initialized = true;
    console.log('SoundService: Initialized successfully');
  }

  /**
   * Preload all sound files into cache
   */
  preloadSounds() {
    console.log('SoundService: Preloading sounds...');
    Object.entries(SOUNDS).forEach(([key, path]) => {
      try {
        const audio = new Audio(path);
        audio.preload = 'auto';
        // Log successful load
        audio.oncanplaythrough = () => {
          console.log(`SoundService: Loaded '${key}'`);
        };
        // Don't throw if file not found, just log
        audio.onerror = () => {
          console.warn(`SoundService: Could not load sound '${key}' from ${path}`);
        };
        this.audioCache[key] = audio;
      } catch (e) {
        console.warn(`SoundService: Failed to create audio for '${key}'`, e);
      }
    });
    console.log(`SoundService: Cache now has ${Object.keys(this.audioCache).length} sounds`);
  }

  /**
   * Check if sounds are enabled
   */
  isEnabled() {
    return storageService.getSetting('soundEnabled', true);
  }

  /**
   * Play a sound by type
   * @param {string} type - Sound type: 'success', 'error', 'click', 'achievement', 'levelup'
   * @param {number} volume - Volume level 0-1 (default: 0.5)
   */
  play(type, volume = 0.5) {
    // Ensure initialized
    if (!this.initialized) {
      this.initialize();
    }
    
    // Check if sounds are enabled
    if (!this.isEnabled()) {
      return;
    }

    const audio = this.audioCache[type];
    if (!audio) {
      console.warn(`SoundService: Unknown sound type '${type}'. Available types: ${Object.keys(this.audioCache).join(', ')}`);
      return;
    }

    try {
      // Clone the audio to allow overlapping sounds
      const sound = audio.cloneNode();
      sound.volume = Math.max(0, Math.min(1, volume));
      
      // Play and handle errors gracefully
      const playPromise = sound.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          // Autoplay may be blocked - this is fine
          console.debug(`SoundService: Could not play '${type}'`, err.message);
        });
      }
    } catch (e) {
      console.debug(`SoundService: Error playing '${type}'`, e);
    }
  }

  /**
   * Play success sound (code runs, correct answer, lesson complete)
   */
  playSuccess() {
    this.play('success', 0.5);
  }

  /**
   * Play error sound (code error, wrong answer)
   */
  playError() {
    this.play('error', 0.4);
  }

  /**
   * Play click sound (button click, toggle)
   */
  playClick() {
    this.play('click', 0.3);
  }

  /**
   * Play achievement sound (badge unlocked)
   */
  playAchievement() {
    this.play('achievement', 0.6);
  }

  /**
   * Play level up sound
   */
  playLevelUp() {
    this.play('levelup', 0.6);
  }

  /**
   * Enable or disable sounds
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    storageService.updateSetting('soundEnabled', enabled);
  }
}

// Export singleton instance
export const soundService = new SoundService();
export default soundService;